// config/helpers.ts
import { ERP_MODULES_CONFIG } from "./core";
import {
  NavigationContext,
  DomainModule,
  LucideIconName,
  DomainGroup,
} from "./types";

/**
 * Helper: Get navOrder for a specific context
 * Handles both number and context-specific navOrder
 */
function getNavOrderForContext(
  navOrder: number | Partial<Record<NavigationContext, number>> | undefined,
  context: NavigationContext,
): number {
  if (navOrder === undefined) return 999;
  if (typeof navOrder === "number") return navOrder;
  return navOrder[context] ?? 999;
}

/**
 * Extrae todos los IDs de módulos del config
 * Incluye tanto los IDs de módulos como las tablas referenciadas
 */
export function getAllModuleIds(): string[] {
  const moduleIds = new Set<string>();
  Object.values(ERP_MODULES_CONFIG.modules).forEach((group) => {
    group.modules.forEach((module) => {
      moduleIds.add(module.id);
      module.tables.forEach((table) => {
        moduleIds.add(table.tableName);
      });
    });
  });
  return Array.from(moduleIds).sort();
}

/**
 * Obtiene un módulo por su ID
 */
export function getModuleById(moduleId: string): DomainModule | undefined {
  for (const group of Object.values(ERP_MODULES_CONFIG.modules)) {
    const module = group.modules.find((m) => m.id === moduleId);
    if (module) return module;
  }
  return undefined;
}

/**
 * Obtiene el grupo al que pertenece un módulo
 */
export function getModuleGroup(moduleId: string): DomainGroup | undefined {
  for (const group of Object.values(ERP_MODULES_CONFIG.modules)) {
    if (group.modules.some((m) => m.id === moduleId)) {
      return group;
    }
  }
  return undefined;
}

/**
 * Verifica si un módulo tiene tabla asociada
 */
export function moduleHasTable(moduleId: string): boolean {
  const module = getModuleById(moduleId);
  return module ? module.tables.length > 0 : false;
}

/**
 * Obtiene todos los módulos de una categoría específica
 */
export function getModulesByGroupId(groupId: string): DomainModule[] {
  const group = ERP_MODULES_CONFIG.modules[groupId];
  return group ? group.modules : [];
}

/**
 * Filtra módulos por contexto de navegación
 */
export function getModulesByContext(
  context: NavigationContext,
): Array<DomainModule & { groupId: string; groupName: string }> {
  const modules: Array<DomainModule & { groupId: string; groupName: string }> =
    [];

  Object.entries(ERP_MODULES_CONFIG.modules).forEach(([groupId, group]) => {
    // Skip groups that are not active
    if (group.status && group.status !== "active") {
      return;
    }

    group.modules.forEach((module) => {
      if (
        module.navigation?.showInNav &&
        module.navigation.contexts.includes(context)
      ) {
        modules.push({
          ...module,
          groupId,
          groupName: group.name,
        });
      }
    });
  });

  // Ordenar por navOrder específico del contexto
  return modules.sort((a, b) => {
    const orderA = getNavOrderForContext(a.navigation?.navOrder, context);
    const orderB = getNavOrderForContext(b.navigation?.navOrder, context);
    return orderA - orderB;
  });
}

/**
 * Obtiene todos los módulos visibles en navegación
 */
export function getNavigationModules(): Array<
  DomainModule & {
    groupId: string;
    groupName: string;
    groupIcon: LucideIconName;
  }
> {
  const modules: Array<
    DomainModule & {
      groupId: string;
      groupName: string;
      groupIcon: LucideIconName;
    }
  > = [];

  Object.entries(ERP_MODULES_CONFIG.modules).forEach(([groupId, group]) => {
    // Skip groups that are not active
    if (group.status && group.status !== "active") {
      return;
    }

    group.modules.forEach((module) => {
      if (module.navigation?.showInNav) {
        modules.push({
          ...module,
          groupId,
          groupName: group.name,
          groupIcon: group.icon,
        });
      }
    });
  });

  return modules;
}

/**
 * Obtiene módulos activos por contexto
 */
export function getActiveModulesByContext(
  context: NavigationContext,
): Array<DomainModule & { groupId: string; groupName: string }> {
  return getModulesByContext(context).filter(
    (module) => module.navigation?.status === "active",
  );
}

/**
 * Obtiene el icono de un módulo
 * Prioriza el icono del módulo, si no existe usa el del grupo
 */
export function getModuleIcon(moduleId: string): LucideIconName | undefined {
  const module = getModuleById(moduleId);
  if (module?.icon) return module.icon;

  const group = getModuleGroup(moduleId);
  return group?.icon;
}

/**
 * Verifica si un módulo está visible en un contexto específico
 */
export function isModuleVisibleInContext(
  moduleId: string,
  context: NavigationContext,
): boolean {
  const module = getModuleById(moduleId);
  return (
    module?.navigation?.showInNav === true &&
    module.navigation.contexts.includes(context)
  );
}

/**
 * Obtiene la primera ruta de un módulo
 */
export function getModulePrimaryRoute(moduleId: string): string | undefined {
  const module = getModuleById(moduleId);
  return module?.routes[0];
}

/**
 * Obtiene todos los grupos ordenados
 */
export function getOrderedGroups(): DomainGroup[] {
  return Object.values(ERP_MODULES_CONFIG.modules).sort(
    (a, b) => a.order - b.order,
  );
}

/**
 * Agrupa módulos por contexto de navegación
 */
export function groupModulesByContext(
  context: NavigationContext,
): Record<string, Array<DomainModule>> {
  const grouped: Record<string, Array<DomainModule>> = {};

  Object.entries(ERP_MODULES_CONFIG.modules).forEach(([groupId, group]) => {
    // Skip groups that are not active
    if (group.status && group.status !== "active") {
      return;
    }

    const modulesInContext = group.modules.filter(
      (module) =>
        module.navigation?.showInNav &&
        module.navigation.contexts.includes(context),
    );

    if (modulesInContext.length > 0) {
      grouped[groupId] = modulesInContext.sort((a, b) => {
        const orderA = getNavOrderForContext(a.navigation?.navOrder, context);
        const orderB = getNavOrderForContext(b.navigation?.navOrder, context);
        return orderA - orderB;
      });
    }
  });

  return grouped;
}

/**
 * Obtiene todas las tablas de grupos activos
 * Solo incluye tablas de grupos con status "active" o sin status definido
 */
export function getTablesFromActiveGroups(): string[] {
  const tables = new Set<string>();
  Object.values(ERP_MODULES_CONFIG.modules).forEach((group) => {
    if (group.status && group.status !== "active") {
      return;
    }
    group.modules.forEach((module) => {
      module.tables.forEach((table) => {
        tables.add(table.tableName);
      });
      module.readOnlyTables.forEach((table) => {
        tables.add(table.tableName);
      });
    });
  });
  return Array.from(tables);
}

/**
 * Verifica si una tabla pertenece a un grupo activo
 */
export function isTableFromActiveGroup(tableName: string): boolean {
  const activeTables = getTablesFromActiveGroups();
  return activeTables.includes(tableName);
}

/**
 * Obtiene los IDs de grupos activos
 */
export function getActiveGroupIds(): string[] {
  return Object.entries(ERP_MODULES_CONFIG.modules)
    .filter(([_, group]) => !group.status || group.status === "active")
    .map(([groupId]) => groupId);
}

/**
 * Obtiene las categorías de notificación de grupos activos
 * Mapea group IDs a categorías de notificación válidas
 */
export function getActiveNotificationCategories(): string[] {
  const activeGroups = getActiveGroupIds();

  // Mapeo de group IDs a categorías de notificación
  const notificationCategoryMap: Record<string, string> = {
    billing: "billing",
    sales: "sales",
    reports: "reports",
    subscriptions: "subscriptions",
    operations: "operations",
    logistics: "logistics",
  };

  return activeGroups
    .filter((groupId) => notificationCategoryMap[groupId])
    .map((groupId) => notificationCategoryMap[groupId]);
}
